import { format } from 'date-fns'
import { ko } from 'date-fns/locale'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef } from 'react'

import type { ReportRes } from '~/@common/api/parentApi'
import { parentApi } from '~/@common/api/parentApi'
import useInfiniteScroll from '~/@common/hooks/useInfiniteScroll'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { toastService, webviewService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import Badge from '~/@common/ui/Badge/Badge'
import Table from '~/@common/ui/Table/Table'

import { ParentReportService } from './ParentReport.service'
import { S } from './ParentReport.style'

const ParentReportPage = observer(() => {
  const { isMobile } = useStudentAppMediaQuery()
  const service = useRepository(ParentReportService)
  const activeChildStudentId = commonRepo.parent.activeChildStudentId

  // 자녀 변경 시 보고서 로드
  useEffect(() => {
    if (!activeChildStudentId) return
    service.fetchReports(activeChildStudentId as string).catch(() => {
      toastService.error('보고서를 불러오지 못했습니다.')
    })
  }, [activeChildStudentId, service])

  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadNextPage = useCallback(() => {
    if (service.hasNextPage && !service.isFetchingNextPage) {
      service.fetchNextPage().catch(() => {
        toastService.error('보고서를 더 불러오지 못했습니다.')
      })
    }
  }, [service.hasNextPage, service.isFetchingNextPage, service])

  useInfiniteScroll(sentinelRef, loadNextPage)

  const { reports, isEmpty, isFetchingNextPage } = service

  return (
    <S.Container>
      {isEmpty && <S.Empty>만들어진 보고서가 없습니다.</S.Empty>}

      {!isMobile && reports.length > 0 && (
        <S.ReportTable>
          <Table.Header ratio="80px auto 444px">
            <Table.HeaderItem>년/월</Table.HeaderItem>
            <Table.HeaderItem alignStart style={{ paddingLeft: 20 }}>
              학습 기간 및 범위
            </Table.HeaderItem>
            <Table.HeaderItem>
              <S.RightSection>
                <span style={{ width: 100, textAlign: 'center' }}>등급</span>
                <S.HeaderDivider />
                <span style={{ width: 308, textAlign: 'center' }}>그래프</span>
              </S.RightSection>
            </Table.HeaderItem>
          </Table.Header>
          <Table.Body>
            {reports.map((report) => (
              <DesktopRow key={report.id} report={report} />
            ))}
            {isFetchingNextPage && <S.Loading>불러오는 중...</S.Loading>}
            <S.Sentinel ref={sentinelRef} />
          </Table.Body>
        </S.ReportTable>
      )}

      {isMobile && reports.length > 0 && (
        <S.CardList>
          {reports.map((report) => (
            <MobileCard key={report.id} report={report} />
          ))}
          {isFetchingNextPage && <S.Loading>불러오는 중...</S.Loading>}
          <S.Sentinel ref={sentinelRef} />
        </S.CardList>
      )}
    </S.Container>
  )
})

export default ParentReportPage

const DONUT_SIZE = 54
const DONUT_STROKE_WIDTH = 3

/** 점수 구간별 도넛 차트 색상 반환 */
const getScoreColor = (score: number): string => {
  if (score >= 80) return 'var(--Colors-Sky-500)'
  if (score >= 60) return 'var(--Colors-Teal-500)'
  if (score > 30) return 'var(--Colors-Orange-300)'
  return 'var(--Colors-Red-400)'
}

/** 보고서 PDF 열기 — 앱: webview, 브라우저: 파일 다운로드 */
const handleReportClick = async (reportId: number, title: string) => {
  try {
    const { presignedUrl, fileName } = await parentApi.getReportPdfUrl(reportId)

    if (webviewService.isApp) {
      webviewService.openPdf(presignedUrl, fileName || title)
    } else {
      const a = document.createElement('a')
      a.href = presignedUrl
      a.download = fileName || title
      a.target = '_blank'
      a.click()
    }
  } catch {
    toastService.error('보고서를 열 수 없습니다.')
  }
}

// ────────────────────────────────────────────
// 컴포넌트: DonutChart (SVG 원형 차트)
// ────────────────────────────────────────────

const DonutChart = ({ score, label }: { score: number; label: string }) => {
  const radius = (DONUT_SIZE - DONUT_STROKE_WIDTH) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const color = getScoreColor(score)

  return (
    <S.DonutItem>
      <svg width={54} height={54}>
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={radius}
          fill="none"
          stroke="var(--Colors-Gray-200)"
          strokeWidth={DONUT_STROKE_WIDTH}
        />
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={DONUT_STROKE_WIDTH}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}
        />
      </svg>
      <span className="donut-value">
        <strong className="donut-value-number">{score}</strong>%
      </span>
      <span className="donut-label">{label}</span>
    </S.DonutItem>
  )
}

/** 보고서 1건의 도넛 차트 — 종합/행동은 항상 표시, 내용은 null이면 숨김 */
const DonutCharts = ({ report }: { report: ReportRes }) => {
  return (
    <S.DonutGroup>
      <DonutChart score={report.metric.correctRate ?? 0} label="종합성취도" />
      <DonutChart score={report.behaviorAreaAverageScore ?? 0} label="행동영역" />
      {report.contentAreaAverageScore !== null && (
        <DonutChart score={report.contentAreaAverageScore ?? 0} label="내용영역" />
      )}
    </S.DonutGroup>
  )
}

// ────────────────────────────────────────────
// PC: 테이블 행
// ────────────────────────────────────────────

const DesktopRow = ({ report }: { report: ReportRes }) => {
  return (
    <Table.Row ratio="80px auto 444px" onClick={() => handleReportClick(report.id, report.title)}>
      <Table.Item>
        {report.year}.{String(report.month).padStart(2, '0')}
      </Table.Item>

      <Table.Item className="period-cell">
        <div className="period-row">
          <span className="period-label">학습기간</span>
          <span className="period-divider" />
          <span className="period-text">
            {format(new Date(report.startDate), 'yyyy.MM.dd(eee)', { locale: ko })} ~{' '}
            {format(new Date(report.endDate), 'yyyy.MM.dd(eee)', { locale: ko })}
          </span>
        </div>
        {report.chapters.length > 0 && (
          <div className="period-row">
            <span className="period-label">학습범위</span>
            <span className="period-divider" />
            <span className="chapter-list">
              {report.chapters.map(({ chapter, revision }, i) => (
                <span key={i} className="chapter-item">
                  <Badge theme={revision === 'CURRICULUM_22' ? 'primaryGray' : 'secondaryGray'}>
                    {revision === 'CURRICULUM_22' ? '22개정' : '15개정'}
                  </Badge>
                  <span className="chapter-text">{chapter}</span>
                </span>
              ))}
            </span>
          </div>
        )}
      </Table.Item>

      <Table.Item>
        <S.RightSection>
          <S.RankCell>
            <span className="rank-number">{report.metric.rank ?? '-'}</span>
            <span className="rank-label">종합등급</span>
          </S.RankCell>
          <S.RowDivider />
          <S.GraphCell>
            <DonutCharts report={report} />
          </S.GraphCell>
        </S.RightSection>
      </Table.Item>
    </Table.Row>
  )
}

// ────────────────────────────────────────────
// MO: 카드
// ────────────────────────────────────────────

const MobileCard = ({ report }: { report: ReportRes }) => {
  return (
    <S.Card onClick={() => handleReportClick(report.id, report.title)}>
      <S.CardTitle>{report.title}</S.CardTitle>

      <S.CardInfoRow>
        <span className="label">학습기간</span>
        <span className="info-divider" />
        <span className="value">
          {format(new Date(report.startDate), 'yyyy.MM.dd(eee)', { locale: ko })} ~{' '}
          {format(new Date(report.endDate), 'yyyy.MM.dd(eee)', { locale: ko })}
        </span>
      </S.CardInfoRow>

      {report.chapters.length > 0 && (
        <S.CardInfoRow>
          <span className="label">학습범위</span>
          <span className="info-divider" />
          <span className="chapter-list">
            {report.chapters.map(({ chapter, revision }, i) => (
              <span key={i} className="chapter-item">
                <Badge theme={revision === 'CURRICULUM_22' ? 'primaryGray' : 'secondaryGray'}>
                  {revision === 'CURRICULUM_22' ? '22개정' : '15개정'}
                </Badge>
                <span className="chapter-text">{chapter}</span>
              </span>
            ))}
          </span>
        </S.CardInfoRow>
      )}

      <S.CardDivider />

      <S.CardBottom>
        <S.RankCell>
          <span className="rank-number">{report.metric.rank ?? '-'}</span>
          <span className="rank-label">종합등급</span>
        </S.RankCell>
        <DonutCharts report={report} />
      </S.CardBottom>
    </S.Card>
  )
}
